/**
 * Week Utilities
 *
 * Utilities for working with ISO week dates and week-based calculations
 * for the planner feature.
 */

import {
  getISOWeek as dateFnsGetISOWeek,
  getISOWeekYear,
  addWeeks as dateFnsAddWeeks,
  startOfISOWeek,
  endOfISOWeek,
  parseISO,
  format,
} from 'date-fns';

/**
 * Get ISO week number for a date
 * @param {Date} date - The date to get the week number for
 * @returns {number} - ISO week number (1-53)
 */
export const getISOWeek = (date) => dateFnsGetISOWeek(date);

/**
 * Get ISO year for a date (may differ from calendar year at year boundaries)
 * @param {Date} date - The date to get the ISO year for
 * @returns {number} - ISO year
 */
export const getISOYear = (date) => getISOWeekYear(date);

/**
 * Format a date as an ISO week string (e.g., '2025-W01')
 * @param {Date} date - The date to format
 * @returns {string} - ISO week string
 */
export const formatISOWeek = (date) => {
  const year = getISOYear(date);
  const week = getISOWeek(date);
  return `${year}-W${String(week).padStart(2, '0')}`;
};

/**
 * Get the number of ISO weeks in a year (52 or 53).
 * A year has 53 ISO weeks if Jan 1 or Dec 31 is a Thursday.
 * @param {number} year - The year
 * @returns {number} - 52 or 53
 */
const getISOWeeksInYear = (year) => {
  const jan1Day = new Date(year, 0, 1).getDay();
  const dec31Day = new Date(year, 11, 31).getDay();
  return (jan1Day === 4 || dec31Day === 4) ? 53 : 52;
};

/**
 * Parse an ISO week string to get the first day (Monday) of that week
 * @param {string} weekString - ISO week string (e.g., '2025-W01')
 * @returns {Date} - The Monday of that week
 */
export const parseISOWeek = (weekString) => {
  const match = weekString.match(/^(\d{4})-W(\d{2})$/);
  if (!match) {
    throw new Error(`Invalid ISO week format: ${weekString}`);
  }

  const year = parseInt(match[1], 10);
  const week = parseInt(match[2], 10);

  // Create a date in the target year (Jan 4 is always in week 1)
  // Use parseISO to avoid timezone issues with the Date constructor
  const jan4 = parseISO(`${year}-01-04`);
  const startOfWeek1 = startOfISOWeek(jan4);

  // Add the appropriate number of weeks
  return dateFnsAddWeeks(startOfWeek1, week - 1);
};

/**
 * Get the current week as an ISO week string
 * @returns {string} - Current ISO week string
 */
export const getCurrentWeek = () => formatISOWeek(new Date());

/**
 * Compare two ISO week strings
 * @param {string} week1 - First ISO week string
 * @param {string} week2 - Second ISO week string
 * @returns {number} - Negative if week1 < week2, 0 if equal, positive if week1 > week2
 */
export const compareWeeks = (week1, week2) => week1.localeCompare(week2);

/**
 * Add weeks to an ISO week string.
 * Uses pure string/number arithmetic to avoid DST-related date conversion
 * issues that can cause week skipping in certain timezones.
 * @param {string} weekString - ISO week string
 * @param {number} weeksToAdd - Number of weeks to add (can be negative)
 * @returns {string} - New ISO week string
 */
export const addWeeks = (weekString, weeksToAdd) => {
  const match = weekString.match(/^(\d{4})-W(\d{2})$/);
  if (!match) return weekString;

  let year = parseInt(match[1], 10);
  let week = parseInt(match[2], 10) + weeksToAdd;

  // Handle forward overflow into next year(s)
  while (week > getISOWeeksInYear(year)) {
    week -= getISOWeeksInYear(year);
    year += 1;
  }

  // Handle backward overflow into previous year(s)
  while (week < 1) {
    year -= 1;
    week += getISOWeeksInYear(year);
  }

  return `${year}-W${String(week).padStart(2, '0')}`;
};

/**
 * Generate an array of ISO week strings between two weeks (inclusive)
 * @param {string} startWeek - Start ISO week string
 * @param {string} endWeek - End ISO week string
 * @returns {string[]} - Array of ISO week strings
 */
export const getWeeksInRange = (startWeek, endWeek) => {
  const weeks = [];
  let currentWeek = startWeek;
  let prevWeek = null;

  while (compareWeeks(currentWeek, endWeek) <= 0) {
    // Safety check: detect if weeks are not advancing (would cause infinite loop)
    if (prevWeek !== null && compareWeeks(currentWeek, prevWeek) <= 0) {
      // eslint-disable-next-line no-console
      console.error(
        `[Planner] Week iteration stuck or went backwards: ${prevWeek} -> ${currentWeek}. `
        + `TZ: ${Intl.DateTimeFormat().resolvedOptions().timeZone}`,
      );
      break;
    }
    weeks.push(currentWeek);
    prevWeek = currentWeek;
    currentWeek = addWeeks(currentWeek, 1);
  }

  return weeks;
};

/**
 * Get the date range for a week
 * @param {string} weekString - ISO week string
 * @returns {{start: Date, end: Date}} - Object with start (Monday) and end (Sunday) dates
 */
export const getWeekDateRange = (weekString) => {
  const start = parseISOWeek(weekString);
  const end = endOfISOWeek(start);

  return { start, end };
};

/**
 * Format a week string for display
 * @param {string} weekString - ISO week string (e.g., '2025-W01')
 * @param {string} formatType - Format type: 'short' (W01) or 'long' (Week 1, 2025)
 * @returns {string} - Formatted week string
 */
export const formatWeekDisplay = (weekString, formatType = 'short') => {
  const match = weekString.match(/^(\d{4})-W(\d{2})$/);
  if (!match) {
    return weekString;
  }

  const year = match[1];
  const week = parseInt(match[2], 10);

  if (formatType === 'short') {
    return `W${week}`;
  }

  if (formatType === 'medium') {
    // Format: "Sep 25-Oct 6, 2025" (shows date range for the week with year)
    // Falls back to original weekString if date parsing fails
    try {
      const weekRange = getWeekDateRange(weekString);
      const startMonth = format(weekRange.start, 'MMM');
      const startDay = format(weekRange.start, 'd');
      const endMonth = format(weekRange.end, 'MMM');
      const endDay = format(weekRange.end, 'd');
      const endYear = format(weekRange.end, 'yyyy');

      if (startMonth === endMonth) {
        // Same month: "Sep 25-30, 2025"
        return `${startMonth} ${startDay}-${endDay}, ${endYear}`;
      }
      // Different months: "Sep 25-Oct 6, 2025"
      return `${startMonth} ${startDay}-${endMonth} ${endDay}, ${endYear}`;
    } catch {
      return weekString;
    }
  }

  return `Week ${week}, ${year}`;
};

/**
 * Convert ISO week string to date range in YYYY-MM-DD format for API
 * @param {string} weekString - ISO week string (e.g., '2025-W01')
 * @returns {{startDate: string, endDate: string}} - Object with API-format dates
 */
export const weekToDateRange = (weekString) => {
  const { start, end } = getWeekDateRange(weekString);

  return {
    startDate: format(start, 'yyyy-MM-dd'),
    endDate: format(end, 'yyyy-MM-dd'),
  };
};

/**
 * Convert a YYYY-MM-DD date string to ISO week format
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {string} - ISO week string (e.g., '2025-W01')
 */
export const dateToWeek = (dateString) => {
  // parseISO treats the date string as a local date without timezone conversion
  const date = parseISO(dateString);
  return formatISOWeek(date);
};

/**
 * Get date range for a week range in YYYY-MM-DD format for API queries
 * @param {string} fromWeek - Start ISO week string
 * @param {string} toWeek - End ISO week string
 * @returns {{startDate: string, endDate: string}} - Object with API-format dates
 */
export const getWeekRangeDates = (fromWeek, toWeek) => {
  const { startDate } = weekToDateRange(fromWeek);
  const { endDate } = weekToDateRange(toWeek);

  return { startDate, endDate };
};

/**
 * Format a week as a compact date range (e.g., "Dec 9-15" or "Dec 30-Jan 5")
 * @param {string} weekString - ISO week string (e.g., '2025-W50')
 * @returns {string} - Compact date range string
 */
export const formatWeekDateRangeCompact = (weekString) => {
  try {
    const { start, end } = getWeekDateRange(weekString);
    const startMonth = format(start, 'MMM');
    const startDay = format(start, 'd');
    const endMonth = format(end, 'MMM');
    const endDay = format(end, 'd');

    if (startMonth === endMonth) {
      return `${startMonth} ${startDay}-${endDay}`;
    }
    return `${startMonth} ${startDay}-${endMonth} ${endDay}`;
  } catch {
    return weekString;
  }
};

/**
 * Get the calendar year from a week string based on the week's start date.
 * This uses the actual start date's year rather than the ISO week year,
 * which provides more intuitive year transitions at year boundaries.
 * @param {string} weekString - ISO week string (e.g., '2025-W01')
 * @returns {number} - The calendar year of the week's start date
 */
export const getYearFromWeek = (weekString) => {
  try {
    const { start } = getWeekDateRange(weekString);
    return start.getFullYear();
  } catch {
    return new Date().getFullYear();
  }
};

/**
 * Format a week's start and end dates separately (e.g., { start: 'Dec 9', end: 'Dec 15' })
 * @param {string} weekString - ISO week string (e.g., '2025-W50')
 * @returns {{start: string, end: string}} - Object with formatted start and end dates
 */
export const formatWeekDatesSeparate = (weekString) => {
  try {
    const { start, end } = getWeekDateRange(weekString);
    return {
      start: format(start, 'MMM d'),
      end: format(end, 'MMM d'),
    };
  } catch {
    return { start: '', end: '' };
  }
};
